import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { circle, distance, point } from '@turf/turf';

import { createSupplementaryPointsForCircle } from './modes.helpers';

import type { DrawModeContext } from './types';

type DirectModeOverrideType = {
  dragFeature: (state: any, e: any, delta: any) => void;
  dragVertex: (state: any, e: any, delta: any) => void;
  onClick?: (state: any, e: any) => void;
  onKeyDown?: (state: any, e: any) => void;
  onKeyUp?: (state: any, e: any) => void;
  onMapClick?: (state: any, e: any) => void;
  onMouseDown?: (state: any, e: any) => void;
  onMouseMove?: (state: any, e: any) => void;
  onMouseOut?: (state: any, e: any) => void;
  onMouseUp?: (state: any, e: any) => void;
  onSetup?: (opts: any) => any;
  onStop?: (state: any) => void;
  onTap?: (state: any, e: any) => void;
  onTouchEnd?: (state: any, e: any) => void;
  onTouchMove?: (state: any, e: any) => void;
  onTouchStart?: (state: any, e: any) => void;
  onTrash?: (state: any) => void;
  toDisplayFeatures: (state: any, geojson: any, push: (feature: any) => void) => void;
};

const DirectModeOverride: DirectModeOverrideType = {
  dragFeature: () => {
    // do nothing
  },
  dragVertex: () => {
    // do nothing
  },
  toDisplayFeatures: () => {
    // do nothing
  }
};

Object.assign(DirectModeOverride, MapboxDraw.modes.direct_select);

DirectModeOverride.dragFeature = function (state: any, e: any, delta: any) {
  if (state.feature?.properties?.isCircle && state.feature?.properties?.disableMove) {
    return;
  }
  const ctx = this as unknown as DrawModeContext;
  MapboxDraw.lib.moveFeatures(ctx.getSelected(), delta);
  ctx
    .getSelected()
    .filter((feature: any) => feature.properties.isCircle)
    .map((circleFeature: any) => circleFeature.properties.center)
    .forEach((center: number[]) => {
      center[0] += delta.lng;
      center[1] += delta.lat;
    });
  state.dragMoveLocation = e.lngLat;
};

DirectModeOverride.dragVertex = function (state: any, e: any, delta: any) {
  if (state.feature.properties.isCircle) {
    const { center } = state.feature.properties;
    const movedVertex = [e.lngLat.lng, e.lngLat.lat];
    const radius = distance(point(center), point(movedVertex), { units: 'kilometers' });
    const circleFeature = circle(center, radius);

    if (circleFeature.geometry) {
      state.feature.incomingCoords(circleFeature.geometry.coordinates);
    }
    state.feature.properties.radiusInKm = radius;
  } else {
    const selectedCoords = state.selectedCoordPaths.map((coordPath: string) => state.feature.getCoordinate(coordPath));
    const selectedCoordPoints = selectedCoords.map((coords: number[]) => ({
      geometry: {
        coordinates: coords,
        type: MapboxDraw.constants.geojsonTypes.POINT
      },
      properties: {},
      type: MapboxDraw.constants.geojsonTypes.FEATURE
    }));

    const constrainedDelta = MapboxDraw.lib.constrainFeatureMovement(selectedCoordPoints, delta);
    for (let i = 0; i < selectedCoords.length; i++) {
      const coord = selectedCoords[i];
      state.feature.updateCoordinate(
        state.selectedCoordPaths[i],
        coord[0] + constrainedDelta.lng,
        coord[1] + constrainedDelta.lat
      );
    }
  }
};

DirectModeOverride.toDisplayFeatures = function (state: any, geojson: any, push: (feature: any) => void) {
  const ctx = this as unknown as DrawModeContext; // Keep this assertion
  if (state.featureId === geojson.properties.id) {
    geojson.properties.active = MapboxDraw.constants.activeStates.ACTIVE;
    push(geojson);
    const supplementaryPoints = geojson.properties.user_isCircle
      ? createSupplementaryPointsForCircle(geojson)
      : // FIX: Removed 'map: ctx.map' from options as lib.createSupplementaryPoints does not accept it
        MapboxDraw.lib.createSupplementaryPoints(geojson, {
          midpoints: true,
          selectedPaths: state.selectedCoordPaths
        });

    if (supplementaryPoints) {
      supplementaryPoints.forEach(push);
    }
  } else {
    geojson.properties.active = MapboxDraw.constants.activeStates.INACTIVE;
    push(geojson);
  }
  ctx.fireActionable(state);
};

export { DirectModeOverride };
